"use client";

import type { ChoghadiyaPeriod } from "@/lib/api/panchang";
import { getQualityColor, getQualityLabel } from "./utils";
import { CHOGHADIYA_RESULT_COPY, type ChoghadiyaLanguage } from "./copy";

interface PeriodRowProps {
  period: ChoghadiyaPeriod;
  language: ChoghadiyaLanguage;
}

// Hindi rows lead with the Devanagari period name; the Latin name is the gloss.
function PeriodRow({ period, language }: PeriodRowProps) {
  const hi = language === "hi" && Boolean(period.name_hi);
  const colors = getQualityColor(period.quality);
  return (
    <div
      className={`flex items-center justify-between p-3 sm:p-4 rounded-lg transition-all ${
        period.is_current ? "ring-2" : ""
      } border border-ish-border-light`}
      style={{
        backgroundColor: period.is_current ? colors.bg : "rgba(0,0,0,0.02)",
        borderColor: period.is_current ? colors.border : undefined,
        outlineColor: period.is_current ? colors.text : undefined,
      }}
    >
      <div className="flex items-center gap-3">
        <span
          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: colors.text }}
        />
        <div>
          <span className="text-body font-semibold text-ish-dark">
            {hi ? period.name_hi : period.name}
          </span>
          <span className="text-caption ml-1.5 text-ish-text-faint">
            {hi ? period.name : period.name_hi}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span
          className="text-caption font-semibold px-2 py-0.5 rounded-full border"
          style={{
            backgroundColor: colors.bg,
            color: colors.text,
            borderColor: colors.border,
          }}
        >
          {getQualityLabel(period.quality, language)}
        </span>
        <span className="text-body-small font-medium tabular-nums text-ish-dark">
          {period.start_time} - {period.end_time}
        </span>
        {period.is_current && (
          <span
            className="text-caption font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
            style={{
              backgroundColor: colors.bg,
              color: colors.text,
            }}
          >
            {CHOGHADIYA_RESULT_COPY[language].now}
          </span>
        )}
      </div>
    </div>
  );
}

interface ChoghadiyaTableProps {
  title: string;
  titleHi: string;
  periods: ChoghadiyaPeriod[];
  timeRange: string;
  language?: ChoghadiyaLanguage;
}

export function ChoghadiyaTable({
  title,
  titleHi,
  periods,
  timeRange,
  language = "en",
}: ChoghadiyaTableProps) {
  return (
    <div className="card-sacred p-6 sm:p-8">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h2 className="text-[18px] font-bold font-serif text-ish-dark">{title}</h2>
          <span className="text-caption text-ish-text-faint">{titleHi}</span>
        </div>
        <span className="text-caption font-medium text-ish-text-muted">{timeRange}</span>
      </div>
      <div className="space-y-2">
        {periods.map((period, i) => (
          <PeriodRow key={`${period.name}-${i}`} period={period} language={language} />
        ))}
      </div>
    </div>
  );
}
